import { CloseIcon } from '../components/icons';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { proposalsApi } from '../utils/api';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';

// PRD-020 F2 — read all three backend error shapes (mirrors PRD-019 F2 helper).
function apiErrorMessage(err, fallback) {
  return (
    err.response?.data?.message ||
    err.response?.data?.error ||
    err.response?.data?.errors?.[0]?.msg ||
    fallback
  );
}

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  proposed: 'bg-blue-100 text-blue-700',
  negotiating: 'bg-yellow-100 text-yellow-700',
  signed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const statusLabels = {
  draft: 'Draft',
  proposed: 'Proposed',
  negotiating: 'Negotiating',
  signed: 'Signed',
  cancelled: 'Cancelled',
};

const DRAFT_KEY = 'newProposalDraft';

export default function Proposals() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [localDraft, setLocalDraft] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // proposal row queued for deletion
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchProposals();
  }, [filter]);

  // Check for unsaved draft in localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        const fd = draft.formData;
        if (fd && (fd.customerName || fd.outcomes?.length > 0 || fd.name)) {
          setLocalDraft(draft);
        }
      }
    } catch { /* ignore corrupt data */ }
  }, []);

  const discardLocalDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setLocalDraft(null);
  };

  const fetchProposals = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await proposalsApi.list(params);
      // RJ rule (2026-07-05): a proposal that became an Agreement lives on
      // the Agreements surface. The default "All" view hides signed
      // proposals; the "Signed" chip keeps the history reachable.
      const rows = response.data.proposals;
      setProposals(filter === 'all' ? rows.filter((p) => p.status !== 'signed') : rows);
    } catch (error) {
      console.error('Failed to fetch proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await proposalsApi.delete(pendingDelete.id);
      setProposals((prev) => prev.filter((p) => p.id !== pendingDelete.id));
      setToast(`Deleted “${pendingDelete.name}”.`);
      setPendingDelete(null);
    } catch (err) {
      setToast(apiErrorMessage(err, 'Failed to delete proposal'));
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="page-header">Proposals</h1>
          <p className="text-gray-600 mt-1">Manage your outcome-based proposals</p>
        </div>
        <Link to="/proposals/new" className="btn-primary mt-4 sm:mt-0">
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          New Proposal
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {['all', 'draft', 'proposed', 'negotiating', 'signed', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              filter === status
                ? 'bg-primary-100 text-primary-700'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            {status === 'all' ? 'All' : statusLabels[status]}
          </button>
        ))}
      </div>

      {/* Unsaved local draft */}
      {localDraft && (filter === 'all' || filter === 'draft') && (
        <div className="card p-4 mb-6 border-dashed border-2 border-gray-300 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                Draft
              </span>
              <div>
                <Link to="/proposals/new" className="text-primary-600 hover:text-primary-700 font-medium">
                  {localDraft.formData.name || `${localDraft.formData.customerName || 'Untitled'} - Proposal`}
                </Link>
                <p className="text-xs text-gray-600">
                  Unsaved draft {localDraft.savedAt && <>&#183; Last edited {new Date(localDraft.savedAt).toLocaleString()}</>}
                  {localDraft.formData.customerName && <> &#183; {localDraft.formData.customerName}</>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/proposals/new" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                Continue
              </Link>
              <button onClick={discardLocalDraft} className="text-sm text-gray-600 hover:text-red-500">
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : proposals.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Proposal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Counterparty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Outcomes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {proposals.map((proposal) => (
                <tr key={proposal.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link to={`/proposals/${proposal.id}`} className="text-primary-600 hover:text-primary-700 font-medium">
                      {proposal.name}
                    </Link>
                    {proposal.template_name && (
                      <p className="text-xs text-gray-600 mt-0.5">From: {proposal.template_name}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {proposal.counterparty?.name || proposal.customer_name || '—'}
                    </div>
                    {proposal.customer_company && !proposal.counterparty?.name && (
                      <div className="text-xs text-gray-600">{proposal.customer_company}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      proposal.myRole === 'seller' ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-50 text-amber-700'
                    }`}>
                      {proposal.myRole === 'seller' ? 'Agency' : 'Client'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[proposal.status]}`}>
                        {statusLabels[proposal.status]}
                      </span>
                      {proposal.shared_with_buyer_at && proposal.status !== 'signed' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-600">
                          shared
                        </span>
                      )}
                      {proposal.seller_signed_at && !proposal.buyer_signed_at && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-600">
                          Agency signed
                        </span>
                      )}
                      {proposal.buyer_signed_at && !proposal.seller_signed_at && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-600">
                          Client signed
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {proposal.outcome_count || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatDate(proposal.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {proposal.myRole === 'seller' && proposal.status !== 'signed' && (
                      <button
                        onClick={() => setPendingDelete(proposal)}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No proposals found</h3>
          <p className="mt-2 text-gray-600">Get started by creating your first outcome-based proposal.</p>
          <Link to="/proposals/new" className="btn-primary mt-6 inline-flex">
            Create Proposal
          </Link>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-3">
          <span>{toast}</span>
          <button onClick={() => setToast(null)} className="text-gray-300 hover:text-white" aria-label="Close"><CloseIcon /></button>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this Proposal?"
        message={pendingDelete
          ? `This permanently removes “${pendingDelete.name}” for both you and ${pendingDelete.counterparty?.name || 'the client'}. This cannot be undone.`
          : ''}
        confirmLabel="Delete Proposal"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Layout>
  );
}
